import 'react-toastify/dist/ReactToastify.css';
import './App.scss';
import i18n from '../i18n';
import { useCallback, useContext, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthenticatedTemplate, useIsAuthenticated } from '@azure/msal-react';
import { msalInstance } from './Components/CustomMsalProvider/CustomMsalProvider';
import { AppContext } from './Context/AppContext';
import { useAppDispatch, useAppSelector } from './Hooks/Hooks';
import { RootState } from './Redux/Store';
import { getContentStackData, setShowBanner } from './Redux/Reducers/ContentOrConfigSlice';
import { retrieveMemberDetails } from './Redux/Reducers/MemberDetailsSlice';
import { TokenManager } from './Utils/TokenManager';
import { isEntitled, processConfigData } from './Utils/ContentOrConfigUtil';
import { showGlobalSearchMobile } from './Utils/Navbar.utils';
import { PageLayout } from './Components/pageLayout/PageLayout';
import Header from './Components/Header/Header';
import Navbar from './Components/Navbar/Navbar';
import Footer from './Components/Footer/Footer';
import AlertBanner from './Components/AlertBanner/AlertBanner';
import ErrorBoundary from './Helpers/ErrorBoundary/ErrorBoundary';
import routeConfig from './routeConfig';
import { getPreferences } from './Redux/Reducers/PreferencesSlice';
import { getNatNalData, resetNatNal } from './Redux/Reducers/NatNalSlice';
import {
  updateSearchQuery,
  handleResetSearchQueryError,
  handleResetSearchQuery,
} from './Redux/Reducers/SearchSlice';
import { createNatNalSearchPayload } from './Utils/App.utils';

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useIsAuthenticated();
  const lang = useAppSelector((state: RootState) => state.global?.language);
  const { loading } = useAppSelector((state: RootState) => state.global);
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const showBanner = useAppSelector((state: RootState) => state.ContentOrConfig?.showBanner);
  const { contentStackData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const { configTableData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const appConfigData = contentStackData?.FPO_APP_CONFIG;
  const { nextAvailableLocation, nextAvailableTime } = useAppSelector(
    (state: RootState) => state.NatNal,
  );
  const { theme, isMobile, module } = useContext(AppContext);
  const getNatNal = useCallback(() => {
    const driverId =
      memberData?.member?.userApplicationDetails?.[0].attributes.PrimaryBookingDriverId;
    if (driverId) {
      dispatch(getNatNalData({ driverId }));
    } else {
      dispatch(resetNatNal());
    }
  }, [dispatch, memberData?.member?.userApplicationDetails]);

  useEffect(() => {
    if (!window?.location?.pathname?.includes('/oo')) {
      window.location.replace('/oo' + window?.location?.pathname);
    }
  }, []);

  useEffect(() => {
    if (nextAvailableLocation && nextAvailableTime) {
      const payload = createNatNalSearchPayload(
        nextAvailableLocation,
        nextAvailableTime,
        appConfigData,
      );
      dispatch(updateSearchQuery(payload));
    } else {
      dispatch(handleResetSearchQuery());
    }
    dispatch(handleResetSearchQueryError());
  }, [nextAvailableLocation, nextAvailableTime]);

  useEffect(() => {
    if (isAuthenticated) {
      TokenManager.init(msalInstance).then((token: any) => {
        const callBack = (_data: any) => {
          !loading &&
            !memberData &&
            dispatch(
              retrieveMemberDetails(
                _data?.config && processConfigData(_data?.config)?.FPO_APP_CONFIG?.APP_INIT,
              ),
            );
          dispatch(
            getContentStackData({
              queryPayload: {
                contentType: 'fpoo_alert_banner,booking_terms,fpoo_footer',
              },
            }),
          );
        };
        dispatch(
          getContentStackData({
            queryPayload: {
              configTable: 'FPO_APP_CONFIG,RATE_GRID_LOOKUP_BY_PAY_TYPE',
              contentType: null,
            },
            callBack: callBack,
          }),
        );
        dispatch(getPreferences(false));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  useEffect(() => {
    if (isAuthenticated) {
      getNatNal();
    }
  }, [getNatNal, isAuthenticated]);
  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);
  useEffect(() => {
    const show_alert_banner =
      contentStackData?.fpoo_alert_banner &&
      contentStackData?.fpoo_alert_banner[0]?.show_alert_banner;
    dispatch(setShowBanner(show_alert_banner));
  }, [contentStackData?.fpoo_alert_banner, dispatch]);
  return (
    <div className={`App ${theme}`} data-testid="App">
      <BrowserRouter basename={'/oo'}>
        <PageLayout>
          <ErrorBoundary>
            <Header />
            <Navbar />
            <div
              className={`App_Page_Container background ${
                isMobile &&
                !isEntitled(
                  memberData?.member,
                  configTableData?.FPO_APP_CONFIG?.APP_INIT,
                  'globalSearch',
                )
                  ? 'globalSearch-hidden'
                  : ''
              } ${
                isMobile && !showGlobalSearchMobile(module, isMobile) ? 'small' : ''
              } ${isMobile && module?.slug === 'manage' ? ' manage' : ''}`}
            >
              <AuthenticatedTemplate>
                {showBanner && <AlertBanner />}
                <Routes>
                  {routeConfig.map((route, index) => {
                    return <Route key={index} path={route.path} element={route.element} />;
                  })}
                </Routes>
              </AuthenticatedTemplate>
            </div>
            <Footer />
          </ErrorBoundary>
        </PageLayout>
      </BrowserRouter>
      <ToastContainer
        position="bottom-left"
        hideProgressBar={true}
        theme="dark"
        draggable={false}
        newestOnTop
        autoClose={1500}
        toastClassName="toast"
        limit={1}
      />
    </div>
  );
}
export default App;
